import { emptySplitApi } from "./emptyApi"

/**
 * Budget update history — the current company's budget / tROAS change ledger
 * read from the `budget-update`, `bid-target-update` and
 * `budget-recommendation` audit history (the Budget Recommender's "History
 * report"). Mirrors the backend `BudgetUpdateReportDto`.
 */

export type BudgetUpdateReportMetric =
  | "Daily budget"
  | "Lifetime budget"
  | "Budget"
  | "tROAS"
  | "tCPA"
  | "CPC ceiling"
  | "Status"

export type BudgetUpdateReportSource =
  | "Budget Recommender"
  | "Direct edit"
  | "Google preview-confirm"

export type BudgetUpdateReportMagnitude = "big" | "small"

/**
 * One ledger line: a single value of a single entity moving from previous to
 * new. A recommender row that moved budget AND tROAS is two lines.
 */
export interface BudgetUpdateReportLine {
  id: string
  auditId: string
  auditType: string
  jobRunId: string | null
  companyId: string
  companyName: string
  /** ISO datetime the change was recorded. */
  changedAt: string
  changedBy: string
  source: BudgetUpdateReportSource
  /** API platform slug (meta | google | tiktok …). */
  platform: string
  /** campaign | adset | portfolio */
  entity: string
  entityId: string
  entityName: string
  campaignName: string
  metric: BudgetUpdateReportMetric
  /** Raw previous value (cents for money, Google fraction for tROAS). */
  previousValue: number | null
  newValue: number | null
  previousLabel: string
  newLabel: string
  /** Percent change; null when previous is unknown or zero. */
  deltaPercent: number | null
  /** new − previous in the raw unit; null when either side is unknown. */
  deltaValue: number | null
  /** "up/down $Δ (±N%)" — both the absolute and the percent move; "" when unreadable. */
  deltaLabel: string
  /**
   * Evaluation-doctrine size: ≥ 20% or ≥ $250/day of daily budget is `big`
   * (must clear the total-spend gate and, on branded search, the
   * incrementality read). Null when there is no readable delta.
   */
  magnitude: BudgetUpdateReportMagnitude | null
  /**
   * Google campaign whose name says Brand / BRD / branded. Platform ROAS is
   * not incrementality evidence for these.
   */
  brandedSearch: boolean
  /** update | pause */
  action: string
  success: boolean
  error: string
  initiativeType: string
  notes: string
}

export interface BudgetUpdateReportCompany {
  id: string
  name: string
}

export interface BudgetUpdateReport {
  from: string
  to: string
  generatedAt: string
  /** The company the report was read for (when it has any line). */
  companies: BudgetUpdateReportCompany[]
  /** Newest first. */
  lines: BudgetUpdateReportLine[]
  truncated: boolean
}

export interface BudgetUpdateReportArgs {
  /** ISO datetime (inclusive). */
  from: string
  /** ISO datetime (inclusive). */
  to: string
}

export const budgetUpdateReportsApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getBudgetUpdateReport: builder.query<
      BudgetUpdateReport,
      BudgetUpdateReportArgs
    >({
      query: ({ from, to }) => ({
        url: "budget-update-reports",
        params: { from, to },
      }),
    }),
  }),
})

export const { useGetBudgetUpdateReportQuery } = budgetUpdateReportsApi
